import { Suspense, useCallback, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Spin } from '@arco-design/web-react';

import { ConfigContext, ConfigProvider } from '@/components/ConfigProvider';
import type { ConfigProviderProps } from '@/components/ConfigProvider/interface';
import Notification from '@/components/Notification';
import TopBar from '@/components/TopNav';
import { getModeMap, resolveModeFromPathname } from '@/components/TopNav/components/ChangeMode';
import { useSystemMenuState, useTenantMenuState } from '@/hooks/useMenu';
import { getModule } from '@/hooks/useRouteScope';
import { STORAGE_KEYS, getDcAndValByTenant, getDcListAndVal, getTenantListAndVal } from '@/layout/servies';
import { getLoginType, getMenuConfig } from '@/pages/login-page/service';
import { shouldLoadTenantResources, shouldVerifySession } from '@/service/auth-recovery';
import { isSessionStorageEvent, subscribeSessionChange } from '@/service/session-sync';
import { yourself } from '@/service/youseself';
import { getTenantClusterByTenant, isValidClusterName, setTenantClusterForTenant } from '@/utils/tenantClusterStorage';
import {
  _getStorage,
  _setStorage,
  getAccessToken,
  getRefeshToken,
  getUserInfo as getStoredUserInfo,
  removeUserInfo,
  setUserInfo,
} from '@/utils/useLocalStorage';

/**
 * 应用程序布局组件
 * 负责应用初始化时的数据加载和状态管理
 */
let timer = null;
const NEED_MODE = ['system', 'console', 'data-center', 'cluster', 'wsp', 'vnc'];
function AppContent() {
  const { clusterList, tenantList, cluster, tenant, tenantCluster, tenantClusters, isLogin, setMode } = useContext(ConfigContext);
  const { menu: systemMenu, loading: systemMenuLoading } = useSystemMenuState();
  const { menu: tenantMenu, loading: tenantMenuLoading } = useTenantMenuState();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const module: any = getModule(location.pathname);
    if (!isLogin || !NEED_MODE.includes(module)) return;

    const urlMode = resolveModeFromPathname(location.pathname);
    if (urlMode) {
      setMode(urlMode);
      return;
    }

    if (systemMenuLoading || tenantMenuLoading) return;

    // 无 URL 模式时依赖 modeMap（需等列表加载），保留短延迟避免误判
    timer && clearTimeout(timer);
    timer = setTimeout(() => {
      const storedMode = _getStorage(STORAGE_KEYS.MODE);
      const modeMap = getModeMap({
        systemMenu,
        clusterList,
        tenantList,
        cluster,
        tenant,
        tenantCluster,
        tenantClusters,
        tenantMenu,
      });
      if (storedMode && modeMap[storedMode]) {
        setMode(storedMode);
      } else {
        navigate('/changeMode');
      }
    }, 100);
    return () => timer && clearTimeout(timer);
  }, [
    isLogin,
    systemMenu,
    systemMenuLoading,
    tenantMenu,
    tenantMenuLoading,
    location.pathname,
    clusterList,
    tenantList,
    cluster,
    tenant,
    tenantCluster,
    tenantClusters,
    navigate,
    setMode,
  ]);

  return (
    <>
      <TopBar />
      <Suspense fallback={<Spin block size={40} style={{ display: 'flex', marginTop: '200px', justifyContent: 'center' }} />}>
        <Outlet />
      </Suspense>
    </>
  );
}

function AppLayout() {
  const { lang, setLang } = useContext(ConfigContext);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const module = getModule(location.pathname);

  // 页面加载状态
  const [initialLoading, setInitialLoading] = useState(true);

  const [config, setConfig] = useState(null);
  const [backMenu, setBackMenu] = useState([]);
  const [policies, setPolicies] = useState([]);

  // 集群与租户列表状态
  const [clusterList, setClusterList] = useState([]);
  const [tenantList, setTenantList] = useState([]);
  const [tenantClusters, setTenantClusters] = useState(_getStorage(STORAGE_KEYS.TENANT_CLUSTERS, []));

  // 首屏按 URL 推断模式，避免 TopBar 挂载后 mode 从 null 闪变
  const [mode, setMode] = useState<ConfigProviderProps['mode']>(() => resolveModeFromPathname(location.pathname) ?? undefined);
  const [cluster, setCluster] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [tenantCluster, setTenantClusterState] = useState(null);
  const [isLogin, setIsLogin] = useState(false);
  const [userInfo, setUserInfoState] = useState(() => getStoredUserInfo());
  const [tenantClustersLoading, setTenantClustersLoading] = useState(true);

  const setTenantCluster = useCallback(
    (value, tenantName = tenant) => {
      // 拒绝非法值，避免 URL 中的 "undefined" 写入状态并与回退逻辑死循环。
      if (!isValidClusterName(value)) return;
      setTenantClusterState(value);
      setTenantClusterForTenant(tenantName, value);
    },
    [tenant],
  );

  /**
   * 获取当前用户信息并写回本地与布局状态。
   * token 仍在时即使 yourself 失败也不清会话，避免误报 401 把顶栏打成幽灵态。
   */
  const fetchCurrentUser = useCallback(async () => {
    if (!shouldVerifySession(getAccessToken(), getRefeshToken())) {
      removeUserInfo();
      setUserInfoState(null);
      return false;
    }
    try {
      const data = (await yourself()) as any;
      if (!data) {
        if (!shouldVerifySession(getAccessToken(), getRefeshToken())) {
          removeUserInfo();
          setUserInfoState(null);
          return false;
        }
        return true;
      }
      setUserInfo(data);
      setUserInfoState(data);
      setPolicies(data.policies);
      return true;
    } catch (error) {
      console.log(error);
      return shouldVerifySession(getAccessToken(), getRefeshToken());
    }
  }, []);

  /** 按 localStorage 对齐 isLogin：无 token 登出，有 token 无 user-info 则回拉 yourself。 */
  const syncSessionFromStorage = useCallback(async () => {
    if (!shouldVerifySession(getAccessToken(), getRefeshToken())) {
      setIsLogin(false);
      setUserInfoState(null);
      removeUserInfo();
      return;
    }
    const cached = getStoredUserInfo();
    if (cached) {
      setIsLogin(true);
      setUserInfoState(cached);
      if (cached.policies) setPolicies(cached.policies);
      return;
    }
    const loggedIn = await fetchCurrentUser();
    setIsLogin(loggedIn);
  }, [fetchCurrentUser]);

  /**
   * 应用初始化主流程
   * 在组件挂载时执行一次
   */
  useEffect(() => {
    initData();
  }, []);

  /**
   * 同页 401 清会话与跨 Tab localStorage 变化时同步登录态，避免顶栏幽灵态。
   */
  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      if (cancelled) return;
      void syncSessionFromStorage();
    };
    const unsubscribe = subscribeSessionChange(sync);
    const onStorage = (event: StorageEvent) => {
      if (!isSessionStorageEvent(event.key)) return;
      sync();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener('storage', onStorage);
    };
  }, [syncSessionFromStorage]);

  /**
   * 监听租户状态变化，当租户被选中后，获取其关联的集群列表
   */
  useEffect(() => {
    updateTenantClusters();
  }, [isLogin, tenant]);

  /**
   * 控制台路由切换时同步 URL 中的租户/数据中心到全局状态。
   * 下单页等场景共用 AppLayout 且不会 remount，需在路由变化时更新导航栏上下文。
   * 同时校正 /cluster/undefined 这类非法路径。
   */
  useEffect(() => {
    if (getModule(location.pathname) !== 'console') return;

    const routeTenant = params?.tenant;
    const routeCluster = params?.cluster;
    if (routeTenant && routeTenant !== 'undefined' && routeTenant !== tenant) {
      setTenant(routeTenant);
      _setStorage(STORAGE_KEYS.TENANT, routeTenant);
    }

    if (isValidClusterName(routeCluster)) {
      if (routeCluster !== tenantCluster) {
        setTenantCluster(routeCluster, routeTenant);
      }
      return;
    }

    // URL 为 undefined / :cluster 等非法值时，尽量替换为已缓存或列表中的合法数据中心
    if (!routeCluster || !routeTenant) return;
    const fallback =
      (isValidClusterName(tenantCluster) && tenantCluster) || getTenantClusterByTenant(routeTenant) || tenantClusters?.[0]?.name;
    if (!isValidClusterName(fallback)) return;

    const nextPath = location.pathname.replace(`/cluster/${routeCluster}`, `/cluster/${fallback}`);
    if (nextPath === location.pathname) return;
    setTenantCluster(fallback, routeTenant);
    navigate(nextPath, { replace: true });
  }, [location.pathname, params?.tenant, params?.cluster, tenant, tenantCluster, tenantClusters, setTenantCluster, navigate]);

  // 并发执行所有初始化数据请求
  const initData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const _isLogin = await fetchCurrentUser();
      setIsLogin(_isLogin);
      try {
        if (_isLogin) {
          await Promise.all([initTenantList(), getConfig(), getMenus(), initDcList(true)]);
        } else {
          await Promise.all([getConfig(), initDcList(false)]);
        }
      } catch (error) {
        console.error(':', error);
      }
    } catch (error) {
      console.error(':', error);
    } finally {
      setInitialLoading(false);
    }
  }, [fetchCurrentUser]);

  /**
   * 获取当前用户的租户列表
   * @async
   */
  const initTenantList = async () => {
    const { data, value } = await getTenantListAndVal({ initialValue: params?.tenant });
    setTenantList(data);
    setTenant(value);
    _setStorage(STORAGE_KEYS.TENANT, value);
    setTenantClusterState(getTenantClusterByTenant(value) || null);
  };

  /**
   * 获取全局配置，例如登录类型
   * @async
   */
  const getConfig = async () => {
    const data = await getLoginType();
    if (data) {
      setConfig(data);
    }
  };

  /**
   * 获取后台菜单配置
   * @async
   */
  const getMenus = async () => {
    const data = await getMenuConfig();
    if (Array.isArray(data)) {
      setBackMenu(data);
    }
  };

  /**
   * 获取当前用户的集群列表
   * @async
   */
  const initDcList = async (_isLogin) => {
    const { data, value } = await getDcListAndVal({
      isLogin: _isLogin,
      initialValue: isValidClusterName(params?.cluster) ? params?.cluster : undefined,
    });
    setClusterList(data);
    if (isValidClusterName(value)) {
      setCluster(value);
      _setStorage(STORAGE_KEYS.CLUSTER, value);
    }
  };

  /**
   * 更新租户集群列表
   * @async
   */
  const updateTenantClusters = async () => {
    if (!shouldLoadTenantResources(isLogin, tenant)) {
      setTenantClustersLoading(false);
      return;
    }
    try {
      setTenantClustersLoading(true);
      const routeCluster = params?.tenant === tenant ? params?.cluster : undefined;
      const cachedTenantCluster = isValidClusterName(routeCluster) ? routeCluster : getTenantClusterByTenant(tenant);
      const { data, value } = await getDcAndValByTenant({
        tenant,
        tenantCluster: cachedTenantCluster,
      });
      setTenantClusters(data);
      if (isValidClusterName(value)) {
        setTenantCluster(value, tenant);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setTenantClustersLoading(false);
    }
  };

  /**
   * 加载动画 JSX
   */
  const LoadingMarkup = <Spin block size={40} style={{ display: 'flex', marginTop: '200px', justifyContent: 'center' }} />;

  return (
    <ConfigProvider
      lang={lang}
      setLang={setLang}
      config={config}
      backMenu={backMenu}
      clusterList={clusterList}
      setClusterList={setClusterList}
      tenantList={tenantList}
      mode={mode}
      setMode={setMode}
      cluster={cluster}
      setCluster={setCluster}
      tenant={tenant}
      setTenant={setTenant}
      tenantCluster={tenantCluster}
      setTenantCluster={setTenantCluster}
      tenantClusters={tenantClusters}
      loading={initialLoading}
      setTenantClusters={setTenantClusters}
      policies={policies}
      userInfo={userInfo}
      isLogin={isLogin}
      tenantClustersLoading={tenantClustersLoading}
    >
      {initialLoading ? LoadingMarkup : <AppContent />}
      {/* 仅数据中心和租户模式展示 */}
      {(module === 'data-center' || module === 'console') && <Notification />}
    </ConfigProvider>
  );
}

export default AppLayout;
